import { QueryClient } from "@tanstack/react-query";

import { NoticeQuery, NoticesQuery } from "lib/generated/app-service-gql/graphql";
import { NoticeDetails } from "./useNotice";
import { NoticesDto, NoticeSearchQueryKey } from "./useNotices";

/**
 * Sets hasBuyerLeads = true for all cached notices whose buyers include any of the given
 * organisation GUIDs. Safe to pass both buyer and supplier IDs — only notices where the
 * ID appears in the buyers array are updated.
 */
export function updateCachedNoticesHasBuyerLeads(
  queryClient: QueryClient,
  organisationIds: string[],
) {
  if (organisationIds.length === 0) return;
  const buyerIdSet = new Set(organisationIds);
  const noticeHasMatchingBuyer = (buyers: { guid: string }[]) =>
    buyers.some((b) => buyerIdSet.has(b.guid));

  queryClient.setQueriesData<NoticesQuery>({ queryKey: ["notices"], exact: false }, (data) => {
    const results = data?.notices?.results;
    if (!results || results.length === 0) return data;

    let anyUpdated = false;
    const updatedResults = results.map((r) => {
      if (r.hasBuyerLeads) return r;
      if (!noticeHasMatchingBuyer(r.buyers)) return r;
      anyUpdated = true;
      return { ...r, hasBuyerLeads: true };
    });

    if (!anyUpdated) return data;

    return {
      ...data,
      notices: {
        ...data.notices,
        results: updatedResults,
      },
    };
  });

  for (const query of queryClient.getQueryCache().findAll({ queryKey: ["notice"], exact: false })) {
    queryClient.setQueryData<NoticeQuery>(query.queryKey, (data) => {
      if (!data?.notice || data.notice.hasBuyerLeads) return data;
      if (!noticeHasMatchingBuyer(data.notice.buyers)) return data;
      return { ...data, notice: { ...data.notice, hasBuyerLeads: true } };
    });
  }
}

export function updateCachedNotice(
  queryClient: QueryClient,
  noticeId: string,
  updateNotice: (r: NoticeDetails) => NoticeDetails,
) {
  queryClient.setQueryData<NoticeQuery>(["notice", noticeId], (resp) => {
    if (!resp?.notice) {
      return resp;
    }

    return { ...resp, notice: updateNotice(resp.notice) };
  });
}

type UpdateCachedNoticesArgs =
  | {
      queryClient: QueryClient;
      lookup: { type: "procurementStageId"; id: string };
      updateNoticesRow: (r: NoticesDto) => NoticesDto;
      updateNoticeQuery?: (r: NoticeDetails) => NoticeDetails;
    }
  | {
      queryClient: QueryClient;
      lookup: { type: "guid"; guid: string };
      updateNoticesRow: (r: NoticesDto) => NoticesDto;
      updateNoticeQuery?: (r: NoticeDetails) => NoticeDetails;
    };
/**
 * Updates the react query cache for notices based on either procurement stage id or notice guid.
 * Can also update the singular notice query too, if the caller specifies an updateNoticeQuery function.
 * @returns {void} Updates the react query cache and does not return a value.
 */
export function updateCachedNotices({
  queryClient,
  lookup,
  updateNoticesRow,
  updateNoticeQuery,
}: UpdateCachedNoticesArgs) {
  let noticeGuidToUpdate: string | null = null;

  queryClient.setQueriesData<NoticesQuery>({ queryKey: ["notices"], exact: false }, (data) => {
    const results = data?.notices?.results;
    if (!results || results.length === 0) return data;

    const index = results.findIndex((r) => {
      if (lookup.type === "procurementStageId") {
        return r.procurementStage?.id === lookup.id;
      }
      return r.guid === lookup.guid;
    });

    if (index === -1) return data;

    // Capture guid once for updating the singular query later
    if (updateNoticeQuery && !noticeGuidToUpdate) {
      noticeGuidToUpdate = results[index].guid;
    }

    const nextRow = updateNoticesRow(results[index]);

    // updater must return the same reference object to signal "no change" to the cache
    if (nextRow === results[index]) return data;

    const nextResults = results.slice();
    nextResults[index] = nextRow;

    return {
      ...data,
      notices: {
        ...data.notices,
        results: nextResults,
      },
    };
  });

  if (updateNoticeQuery && noticeGuidToUpdate) {
    updateCachedNotice(queryClient, noticeGuidToUpdate, updateNoticeQuery);
  }
}

/**
 * Updates both the singular notice cache (by guid) and the notices list cache
 * (by procurementStageId). Use this when a mutation on a notice needs to stay in sync
 * across both query contexts — e.g. comment create/update/delete on the notice details page.
 */
export function updateCachedNoticeAndList(
  queryClient: QueryClient,
  notice: { guid: string; procurementStage: { id: string } },
  noticeUpdater: (n: NoticeDetails) => NoticeDetails,
  rowUpdater: (r: NoticesDto) => NoticesDto,
) {
  updateCachedNotice(queryClient, notice.guid, noticeUpdater);
  updateCachedNotices({
    queryClient,
    lookup: { type: "procurementStageId", id: notice.procurementStage.id },
    updateNoticesRow: rowUpdater,
  });
}

/**
 * Sets `isHidden` on a notice in the notice details cache and in every cached notice search.
 * Hiding a notice also drops it from cached searches that exclude hidden notices.
 */
export function updateCachedNoticeHidden(
  queryClient: QueryClient,
  notice: { guid: string; procurementStage: { id: string } },
  isHidden: boolean,
) {
  updateCachedNotice(queryClient, notice.guid, (n) => ({ ...n, isHidden }));

  queryClient.getQueriesData<NoticesQuery>(["notices"]).forEach(([key, data]) => {
    if (!data?.notices.results.some((r) => r.procurementStage.id === notice.procurementStage.id)) {
      return;
    }
    const { results } = data.notices;

    const [, searchParams] = key as NoticeSearchQueryKey;
    const removeRow = isHidden && searchParams.includeHiddenNotices === false;

    queryClient.setQueryData<NoticesQuery>(key, {
      ...data,
      notices: {
        ...data.notices,
        results: removeRow
          ? results.filter((r) => r.procurementStage.id !== notice.procurementStage.id)
          : results.map((r) =>
              r.procurementStage.id === notice.procurementStage.id ? { ...r, isHidden } : r,
            ),
      },
    });
  });
}
